import React, { ReactNode, useCallback } from 'react';

import { Route, Redirect, Switch, useHistory, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';

import Loader from '@uikit/old/Loader/Loader';
import ResponaNewLogo from '@uikit/old/ResponaNewLogo/ResponaNewLogo';

import { redirectUserTo } from '@utils/historyHandler';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import queryCacheKeysConstants from '@constants/queryCacheKeys.constants';
import urls from '@constants/urls';

import {
  getOrganizationOnboardingDetailsApi,
  updateOrganizationOnboardingDetailsApi,
} from '@api/userOrganization.api';

import { OrganizationOnboardingType } from '@ts/userOrganization.types';
import { OrganizationOnboardingDetails } from 'respona_api/generated/user-organization_pb';
import { RealmTypeMap } from 'respona_api/generated/common_pb';
import { isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';

import './BaseOnboardingFlow.scss';
import LoginPage from '@pages/LoginPage/LoginPage';
import { SVGIcon } from '@uikit/old/Icon/Icon';

type RenderRoutesArgs<TRealmPayload> = {
  details?: OrganizationOnboardingType;
  onSave: (payload: Partial<TRealmPayload>, isCompleted: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
};

type BaseOnboardingFlowProps<TRealmPayload> = {
  realm: RealmTypeMap[keyof RealmTypeMap];
  cacheKey: keyof NonNullable<OrganizationOnboardingType>;
  selectRealmDetails: (data?: OrganizationOnboardingType) => Partial<TRealmPayload> | undefined;
  buildRealmRequest: (merged: Partial<TRealmPayload>) => any;
  attachRealmRequest: (root: OrganizationOnboardingDetails, realmReq: any) => void;
  stepPaths: string[];
  renderRoutes: (args: RenderRoutesArgs<TRealmPayload>) => ReactNode;
};

function BaseOnboardingFlow<TRealmPayload>(
  props: BaseOnboardingFlowProps<TRealmPayload>
): JSX.Element {
  const {
    realm,
    cacheKey,
    selectRealmDetails,
    buildRealmRequest,
    attachRealmRequest,
    stepPaths,
    renderRoutes,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();

  const { data: details } = useQuery<OrganizationOnboardingType>(
    queryCacheKeysConstants.organizationOnboarding,
    () => getOrganizationOnboardingDetailsApi(),
    {
      enabled: true,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  const currentIndex = stepPaths.findIndex((p) => location.pathname.startsWith(p));

  const goNext = useCallback(() => {
    const next = currentIndex >= 0 ? currentIndex + 1 : 0;
    if (next < stepPaths.length) {
      redirectUserTo(history, stepPaths[next]);
    } else if (isConciergeRealm()) {
      redirectUserTo(history, urls.ORDERS(currentWorkspaceId));
    } else if (isPublisherRealm()) {
      redirectUserTo(history, urls.WEBSITES(currentWorkspaceId));
    }
  }, [currentIndex, history, stepPaths, realm, currentWorkspaceId]);

  const goBack = useCallback(() => {
    const prev = currentIndex > 0 ? currentIndex - 1 : 0;
    redirectUserTo(history, stepPaths[prev]);
  }, [currentIndex, history, stepPaths]);

  const onDetailsUpdate = (payload: Partial<TRealmPayload>, isCompleted: boolean) => {
    const merged: Partial<TRealmPayload> = {
      ...(selectRealmDetails(details) || {}),
      ...payload,
    };

    const request = new OrganizationOnboardingDetails();
    request.setRealm(realm);
    request.setCompleted(isCompleted);
    const realmRequest = buildRealmRequest(merged);
    attachRealmRequest(request, realmRequest);

    updateOrganizationOnboardingDetailsApi(request).then(() => {
      queryClient.setQueryData(queryCacheKeysConstants.organizationOnboarding, (prev: any) => ({
        ...(prev || {}),
        [cacheKey]: {
          ...(prev?.[cacheKey] || {}),
          ...merged,
        },
      }));
    });
  };

  return (
    <div>
      <Loader withTopMargin>
        <div className="onboarding-flow__background">
          <LoginPage history={history} location={{ pathname: '/welcome' }} />
        </div>
        <div className="onboarding-flow">
          <div className="onboarding-flow__modal">
            <div className="onboarding-flow__header">
              <div className="onboarding-flow__logo">
                <ResponaNewLogo size="big" />
                <div className="star-icon">
                  <SVGIcon icon="purpleStar" />
                </div>
              </div>
            </div>

            <Switch>
              {renderRoutes({
                details,
                onSave: onDetailsUpdate,
                onBack: goBack,
                onContinue: goNext,
                onSkip: goNext,
              })}
              <Route path="*">
                <Redirect to={stepPaths[0]} />
              </Route>
            </Switch>
          </div>
        </div>
      </Loader>
    </div>
  );
}

export default BaseOnboardingFlow;
